/**
 * SyproFlow Admin Dashboard v1.0.0
 * ==================================
 * Password-protected dashboard showing usage, feedback, errors, chat logs.
 * 
 * Usage: <AdminDashboard /> — renders as a full-page overlay
 * Toggle via: onClose callback
 */

import React, { useState, useEffect, useCallback } from 'react';

const getApiBase = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.syproflow.com' || host === 'syproflow.com' || host === 'www.syproflow.com') {
      return 'https://api.syproflow.com';
    }
  }
  return import.meta?.env?.VITE_API_BASE_URL || 'http://localhost:8000';
};

const API = getApiBase();

export default function AdminDashboard({ onClose }) {
  const [adminKey, setAdminKey] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [chatLogs, setChatLogs] = useState(null);
  const [feedbackList, setFeedbackList] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [days, setDays] = useState(7);
  const [geoData, setGeoData] = useState({});

  // Resolve IPs to city/ISP using free ip-api.com (batch)
  const resolveIPs = useCallback(async (data) => {
    const ips = new Set();
    (data?.top_users || []).forEach(u => u.ip_address && ips.add(u.ip_address));
    (data?.recent_extractions || []).forEach(r => r.ip_address && ips.add(r.ip_address));
    
    const newGeo = { ...geoData };
    const toResolve = [...ips].filter(ip => !newGeo[ip] && ip !== 'unknown');
    
    // ip-api.com supports batch of up to 100 IPs
    if (toResolve.length > 0) {
      try {
        const res = await fetch('http://ip-api.com/batch?fields=query,city,regionName,country,isp', {
          method: 'POST',
          body: JSON.stringify(toResolve.map(ip => ({ query: ip })))
        });
        if (res.ok) {
          const results = await res.json();
          results.forEach(r => {
            if (r.city) {
              newGeo[r.query] = { city: r.city, region: r.regionName, country: r.country, isp: r.isp };
            }
          });
        }
      } catch (e) {
        // Fallback: resolve one by one
        for (const ip of toResolve.slice(0, 10)) {
          try {
            const res = await fetch(`http://ip-api.com/json/${ip}?fields=query,city,regionName,country,isp`);
            if (res.ok) {
              const r = await res.json();
              if (r.city) newGeo[r.query] = { city: r.city, region: r.regionName, country: r.country, isp: r.isp };
            }
          } catch (_) {}
        }
      }
      setGeoData(newGeo);
    }
  }, [geoData]);

  // Helper to format geo info
  const geoLabel = (ip) => {
    const g = geoData[ip];
    if (!g) return '';
    return `${g.city}, ${g.region}`;
  };
  const geoISP = (ip) => {
    const g = geoData[ip];
    return g?.isp || '';
  };

  const fetchDashboard = useCallback(async (key, period) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/v1/admin/dashboard?key=${encodeURIComponent(key)}&days=${period}`);
      if (res.status === 403) { setError('Invalid admin key'); setAuthenticated(false); return; }
      if (!res.ok) throw new Error('Failed to load dashboard');
      const data = await res.json();
      setDashboard(data);
      setAuthenticated(true);
      resolveIPs(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChatLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/v1/admin/chat-logs?key=${encodeURIComponent(adminKey)}&limit=50`);
      if (res.ok) setChatLogs(await res.json());
    } catch (e) { console.error(e); }
  }, [adminKey]);

  const fetchFeedback = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/v1/admin/feedback?key=${encodeURIComponent(adminKey)}&days=30`);
      if (res.ok) setFeedbackList(await res.json());
    } catch (e) { console.error(e); }
  }, [adminKey]);

  const handleLogin = () => {
    if (!adminKey.trim()) return;
    fetchDashboard(adminKey, days);
  };

  useEffect(() => {
    if (authenticated) {
      fetchDashboard(adminKey, days);
    }
  }, [days]);

  useEffect(() => {
    if (authenticated && activeTab === 'chats' && !chatLogs) fetchChatLogs();
    if (authenticated && activeTab === 'feedback' && !feedbackList) fetchFeedback();
  }, [activeTab, authenticated]);

  // Stat card component
  const StatCard = ({ label, value, sub, color = '#ea580c' }) => (
    <div style={{
      background: '#fff', borderRadius: '12px', padding: '20px',
      border: '1px solid #e5e7eb', flex: '1 1 200px', minWidth: '180px'
    }}>
      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{sub}</div>}
    </div>
  );

  // Table component
  const DataTable = ({ headers, rows }) => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb',
                color: '#374151', fontWeight: 600, background: '#f9fafb', whiteSpace: 'nowrap'
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
              {row.map((cell, j) => (
                <td key={j} style={{
                  padding: '10px 12px', color: '#4b5563',
                  maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Login screen
  if (!authenticated) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 20000, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{
          background: '#fff', borderRadius: '16px', padding: '40px',
          width: '400px', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '22px', color: '#111' }}>🔐 Admin Dashboard</h2>
          <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: '14px' }}>Enter admin key to access</p>
          
          {error && <div style={{
            background: '#fef2f2', color: '#dc2626', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
          }}>❌ {error}</div>}
          
          <input
            type="password"
            value={adminKey}
            onChange={e => setAdminKey(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleLogin(); }}
            placeholder="Admin key..."
            autoFocus
            style={{
              width: '100%', border: '1px solid #d1d5db', borderRadius: '10px',
              padding: '12px 16px', fontSize: '15px', outline: 'none',
              marginBottom: '16px', boxSizing: 'border-box'
            }}
          />
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleLogin} disabled={loading}
              style={{
                flex: 1, background: '#ea580c', color: '#fff', border: 'none',
                borderRadius: '10px', padding: '12px', fontSize: '15px', fontWeight: 600,
                cursor: 'pointer', opacity: loading ? 0.6 : 1
              }}
            >{loading ? 'Loading...' : 'Access Dashboard'}</button>
            <button onClick={onClose}
              style={{
                background: '#f3f4f6', color: '#374151', border: 'none',
                borderRadius: '10px', padding: '12px 20px', fontSize: '15px', cursor: 'pointer'
              }}
            >Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  const u = dashboard?.usage || {};
  const fb = dashboard?.feedback || {};
  const err = dashboard?.errors || {};
  const ch = dashboard?.chat || {};

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 20000, background: '#f1f5f9',
      overflowY: 'auto'
    }}>
      {/* Header */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '16px',
        position: 'sticky', top: 0, zIndex: 1
      }}>
        <h1 style={{ margin: 0, fontSize: '20px', color: '#111', flex: 1 }}>
          📊 SyproFlow Admin Dashboard
        </h1>
        
        {/* Period selector */}
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          style={{
            border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px 12px',
            fontSize: '13px', cursor: 'pointer'
          }}
        >
          <option value={1}>Today</option>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
        
        <button onClick={() => fetchDashboard(adminKey, days)}
          style={{
            background: '#ea580c', color: '#fff', border: 'none', borderRadius: '8px',
            padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: 500
          }}
        >↻ Refresh</button>
        
        <button onClick={onClose}
          style={{
            background: '#f3f4f6', border: 'none', borderRadius: '8px',
            padding: '8px 16px', fontSize: '13px', cursor: 'pointer'
          }}
        >✕ Close</button>
      </div>

      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading dashboard...</div>}
        
        {!loading && dashboard && (
          <>
            {/* KPI Cards */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
              <StatCard label="Total Extractions" value={u.total_extractions || 0} sub={`${u.unique_users || 0} unique users`} />
              <StatCard label="Pages Processed" value={u.total_pages || 0} sub={`₹${u.total_cost_inr || 0} total cost`} color="#2563eb" />
              <StatCard label="Feedback" value={`👍 ${fb.thumbs_up || 0} · 👎 ${fb.thumbs_down || 0}`}
                sub={`${fb.total || 0} total responses`} color="#059669" />
              <StatCard label="Errors" value={err.total || 0}
                sub={`${u.success_count || 0} successful`} color={err.total > 0 ? '#dc2626' : '#059669'} />
              <StatCard label="Chat Messages" value={ch.total_messages || 0}
                sub={`${ch.unique_users || 0} chat users`} color="#7c3aed" />
            </div>

            {/* Tabs */}
            <div style={{
              display: 'flex', gap: '4px', marginBottom: '20px',
              borderBottom: '2px solid #e5e7eb', paddingBottom: '0'
            }}>
              {[
                ['overview', '📈 Overview'],
                ['users', '👥 Users'],
                ['feedback', '📝 Feedback'],
                ['errors', '🚨 Errors'],
                ['chats', '💬 Chat Logs']
              ].map(([key, label]) => (
                <button key={key} onClick={() => setActiveTab(key)}
                  style={{
                    background: activeTab === key ? '#ea580c' : 'transparent',
                    color: activeTab === key ? '#fff' : '#6b7280',
                    border: 'none', borderRadius: '8px 8px 0 0',
                    padding: '10px 20px', fontSize: '13px', fontWeight: 500,
                    cursor: 'pointer', transition: 'all 0.15s'
                  }}
                >{label}</button>
              ))}
            </div>

            {/* Tab Content */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
              
              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <>
                  <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#111' }}>Daily Extraction Trend</h3>
                  {u.by_day && u.by_day.length > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '120px', marginBottom: '24px' }}>
                      {u.by_day.map((d, i) => {
                        const max = Math.max(...u.by_day.map(x => x.extractions));
                        const h = max > 0 ? (d.extractions / max) * 100 : 0;
                        return (
                          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#6b7280' }}>{d.extractions}</span>
                            <div style={{
                              width: '100%', maxWidth: '40px', height: `${Math.max(h, 4)}%`,
                              background: 'linear-gradient(to top, #ea580c, #fb923c)',
                              borderRadius: '4px 4px 0 0', transition: 'height 0.3s'
                            }} />
                            <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                              {d.date?.slice(5)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : <p style={{ color: '#9ca3af' }}>No data for this period</p>}

                  <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#111' }}>Model Usage</h3>
                  {u.by_model && u.by_model.length > 0 ? (
                    <DataTable
                      headers={['Model', 'Extractions', 'Pages', 'Cost (₹)']}
                      rows={u.by_model.map(m => [
                        m.model_used || 'Unknown',
                        m.count,
                        m.pages || 0,
                        `₹${(m.cost || 0).toFixed(2)}`
                      ])}
                    />
                  ) : <p style={{ color: '#9ca3af' }}>No model data</p>}

                  <h3 style={{ margin: '20px 0 12px', fontSize: '16px', color: '#111' }}>Recent Extractions</h3>
                  {dashboard.recent_extractions?.length > 0 ? (
                    <DataTable
                      headers={['Time', 'IP', 'Location', 'File', 'Type', 'Model', 'Pages', 'Cost', 'Status']}
                      rows={dashboard.recent_extractions.map(r => [
                        r.created_at?.slice(0, 16) || '-',
                        r.ip_address?.slice(0, 15) || '-',
                        geoLabel(r.ip_address) || '...',
                        r.filename?.slice(0, 25) || '-',
                        r.document_type || '-',
                        r.model_used || '-',
                        r.page_count || 0,
                        `₹${(r.cost_inr || 0).toFixed(2)}`,
                        r.status === 'success' ? '✅' : '❌'
                      ])}
                    />
                  ) : <p style={{ color: '#9ca3af' }}>No recent extractions</p>}
                </>
              )}

              {/* USERS TAB */}
              {activeTab === 'users' && (
                <>
                  <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#111' }}>Top Users by Extraction Count</h3>
                  {dashboard.top_users?.length > 0 ? (
                    <DataTable
                      headers={['IP Address', 'Location', 'ISP', 'Extractions', 'Pages', 'Cost (₹)', 'Last Active']}
                      rows={dashboard.top_users.map(u => [
                        u.ip_address || '-',
                        geoLabel(u.ip_address) || '...',
                        geoISP(u.ip_address) || '...',
                        u.extractions,
                        u.pages || 0,
                        `₹${(u.cost || 0).toFixed(2)}`,
                        u.last_used?.slice(0, 16) || '-'
                      ])}
                    />
                  ) : <p style={{ color: '#9ca3af' }}>No users yet</p>}
                </>
              )}

              {/* FEEDBACK TAB */}
              {activeTab === 'feedback' && (
                <>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                    <StatCard label="👍 Positive" value={fb.thumbs_up || 0} color="#059669" />
                    <StatCard label="👎 Negative" value={fb.thumbs_down || 0} color="#dc2626" />
                    <StatCard label="💬 With Comments" value={fb.with_comments || 0} color="#2563eb" />
                  </div>

                  <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#111' }}>All Feedback</h3>
                  {feedbackList?.feedback?.length > 0 ? (
                    <DataTable
                      headers={['Time', 'Rating', 'Comment', 'Document', 'Model', 'Pages']}
                      rows={feedbackList.feedback.map(f => [
                        f.created_at?.slice(0, 16) || '-',
                        f.rating === 'up' ? '👍' : '👎',
                        f.comment || '-',
                        f.document_name?.slice(0, 25) || '-',
                        f.model_used || '-',
                        f.page_count || '-'
                      ])}
                    />
                  ) : <p style={{ color: '#9ca3af' }}>No feedback yet. Feedback appears after users rate extractions.</p>}
                </>
              )}

              {/* ERRORS TAB */}
              {activeTab === 'errors' && (
                <>
                  <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#111' }}>Error Summary</h3>
                  {err.by_type?.length > 0 ? (
                    <DataTable
                      headers={['Error Type', 'Count']}
                      rows={err.by_type.map(e => [e.error_type || 'Unknown', e.count])}
                    />
                  ) : <p style={{ color: '#059669', fontWeight: 500 }}>✅ No errors in this period!</p>}

                  {err.recent?.length > 0 && (
                    <>
                      <h3 style={{ margin: '20px 0 12px', fontSize: '16px', color: '#111' }}>Recent Errors</h3>
                      <DataTable
                        headers={['Time', 'Type', 'Message', 'IP']}
                        rows={err.recent.map(e => [
                          e.created_at?.slice(0, 16) || '-',
                          e.error_type || '-',
                          (e.error_message || '-').slice(0, 60),
                          e.ip_address?.slice(0, 15) || '-'
                        ])}
                      />
                    </>
                  )}
                </>
              )}

              {/* CHAT LOGS TAB */}
              {activeTab === 'chats' && (
                <>
                  <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#111' }}>
                    Recent Chat Conversations ({chatLogs?.total || 0} messages)
                  </h3>
                  {chatLogs?.chats?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {chatLogs.chats.map((c, i) => (
                        <div key={i} style={{
                          border: '1px solid #e5e7eb', borderRadius: '10px',
                          padding: '14px', background: '#fafafa'
                        }}>
                          <div style={{
                            display: 'flex', justifyContent: 'space-between',
                            marginBottom: '8px', fontSize: '11px', color: '#9ca3af'
                          }}>
                            <span>IP: {c.ip || 'unknown'}</span>
                            <span>{c.timestamp?.slice(0, 16)}</span>
                          </div>
                          <div style={{ fontSize: '13px' }}>
                            <div style={{ color: '#ea580c', fontWeight: 500, marginBottom: '4px' }}>
                              👤 {c.user_message}
                            </div>
                            <div style={{ color: '#374151' }}>
                              🤖 {c.bot_reply?.slice(0, 200)}{c.bot_reply?.length > 200 ? '...' : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p style={{ color: '#9ca3af' }}>No chat conversations yet.</p>}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
